import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Send, Plus, Trash2, Loader2, Users, CheckCircle2, Clock, X, Upload } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import EventSubNav from '../../components/EventSubNav';
import { eventsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

const EventInvitationsPage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    emails: '',
    quantity: 1,  // Default quantity per email
    subject: '',
    message: ''
  });
  const [bulkEmails, setBulkEmails] = useState('');

  useEffect(() => {
    fetchData();
  }, [eventId]);

  const fetchData = async () => {
    try {
      const [eventRes, invitationsRes] = await Promise.all([
        eventsApi.getById(eventId),
        eventsApi.getInvitations(eventId).catch(() => ({ data: [] }))
      ]);
      setEvent(eventRes.data);
      setInvitations(invitationsRes.data || []);
      
      // Set default subject
      setFormData(prev => ({
        ...prev,
        subject: prev.subject || `Invitación: ${eventRes.data.title}`
      }));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvitations = async (e) => {
    e.preventDefault();
    
    // Parse emails from textarea (comma or newline separated)
    const emailList = formData.emails
      .split(/[,\n]/)
      .map(e => e.trim())
      .filter(e => e && e.includes('@'));
    
    if (emailList.length === 0) {
      toast.error('Ingresa al menos un email válido');
      return;
    }

    const quantity = parseInt(formData.quantity) || 1;
    if (quantity < 1 || quantity > 20) {
      toast.error('La cantidad debe ser entre 1 y 20');
      return;
    }

    setSending(true);
    try {
      const response = await eventsApi.sendInvitations(eventId, {
        emails: emailList,
        quantity: quantity,  // Send quantity per email
        subject: formData.subject,
        message: formData.message
      });
      
      const totalInvitations = emailList.length * quantity;
      toast.success(`${response.data.sent || totalInvitations} invitaciones enviadas`);
      setFormData({ ...formData, emails: '', quantity: 1 });
      setShowForm(false);
      fetchData();
    } catch (error) {
      console.error('Error sending invitations:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar invitaciones');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteInvitation = async (invitationId) => {
    if (!confirm('¿Eliminar esta invitación?')) return;
    
    try {
      await eventsApi.deleteInvitation(eventId, invitationId);
      toast.success('Invitación eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleResend = async (invitationId) => {
    try {
      await eventsApi.resendInvitation(eventId, invitationId);
      toast.success('Invitación reenviada');
      fetchData();
    } catch (error) {
      toast.error('Error al reenviar');
    }
  };

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-[#1A1A1A] w-1/3" />
            <div className="h-40 bg-[#1A1A1A]" />
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  const stats = {
    total: invitations.length,
    sent: invitations.filter(i => i.status === 'sent').length,
    opened: invitations.filter(i => i.status === 'opened').length,
    confirmed: invitations.filter(i => i.status === 'confirmed').length
  };

  return (
    <SidebarProvider>
      <AdminLayout>
        <EventSubNav eventTitle={event?.title} />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          data-testid="invitations-admin"
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl font-black uppercase tracking-tight">Invitaciones</h2>
              <p className="text-[#A1A1AA] text-sm">Envía invitaciones por email a tus invitados</p>
            </div>
            <Button 
              onClick={() => setShowForm(!showForm)}
              className="bg-white text-black hover:bg-[#E5E5E5]"
            >
              <Mail size={18} className="mr-2" />
              Nueva Invitación
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="card-dashboard text-center">
              <p className="text-3xl font-black">{stats.total}</p>
              <p className="text-xs uppercase tracking-wider text-[#52525B]">Total</p>
            </div>
            <div className="card-dashboard text-center">
              <p className="text-3xl font-black text-blue-500">{stats.sent}</p>
              <p className="text-xs uppercase tracking-wider text-[#52525B]">Enviadas</p>
            </div>
            <div className="card-dashboard text-center">
              <p className="text-3xl font-black text-yellow-500">{stats.opened}</p>
              <p className="text-xs uppercase tracking-wider text-[#52525B]">Abiertas</p>
            </div>
            <div className="card-dashboard text-center">
              <p className="text-3xl font-black text-green-500">{stats.confirmed}</p>
              <p className="text-xs uppercase tracking-wider text-[#52525B]">Confirmadas</p>
            </div>
          </div>

          {/* Send Form */}
          {showForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="card-dashboard mb-8"
            >
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Send size={18} />
                Enviar Invitaciones
              </h3>
              
              <form onSubmit={handleSendInvitations} className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#52525B]">
                    Emails (separados por coma o línea)
                  </Label>
                  <Textarea
                    value={formData.emails}
                    onChange={(e) => setFormData({ ...formData, emails: e.target.value })}
                    placeholder="email1@ejemplo.com, email2@ejemplo.com..."
                    className="bg-transparent border-[#27272A] min-h-[100px]"
                    required
                    data-testid="invitation-emails-input"
                  />
                </div>
                
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#52525B]">
                    Cantidad de Invitaciones por Email
                  </Label>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      min="1"
                      max="20"
                      className="bg-transparent border-[#27272A] w-24"
                      data-testid="invitation-quantity-input"
                    />
                    <span className="text-sm text-[#A1A1AA]">
                      {formData.quantity > 1 
                        ? `Se enviarán ${formData.quantity} entradas a cada email`
                        : 'Se enviará 1 entrada a cada email'
                      }
                    </span>
                  </div>
                  <p className="text-xs text-[#52525B] mt-1">
                    Útil para invitar familias o grupos
                  </p>
                </div>
                
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#52525B]">
                    Asunto del Email
                  </Label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Invitación: Nombre del Evento"
                    className="bg-transparent border-[#27272A]"
                    required
                    data-testid="invitation-subject-input"
                  />
                </div>
                
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#52525B]">
                    Mensaje Personalizado (opcional)
                  </Label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Agrega un mensaje personalizado para tus invitados..."
                    className="bg-transparent border-[#27272A] min-h-[100px]"
                  />
                </div>
                
                <div className="flex gap-4 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowForm(false)}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={sending}
                    className="flex-1 bg-white text-black hover:bg-[#E5E5E5]"
                  >
                    {sending ? (
                      <Loader2 size={18} className="animate-spin mr-2" />
                    ) : (
                      <Send size={18} className="mr-2" />
                    )}
                    Enviar Invitaciones
                  </Button>
                </div>
              </form>
            </motion.div>
          )}

          {/* Invitations List */}
          {invitations.length === 0 ? (
            <div className="card-dashboard text-center py-16">
              <Mail size={48} className="mx-auto mb-4 text-[#A1A1AA]" />
              <h3 className="text-xl font-black uppercase mb-2">Sin invitaciones</h3>
              <p className="text-[#A1A1AA] mb-6">
                No has enviado invitaciones para este evento aún.
              </p>
              <Button onClick={() => setShowForm(true)} className="bg-white text-black hover:bg-[#E5E5E5]">
                <Mail size={18} className="mr-2" />
                Enviar Primera Invitación
              </Button>
            </div>
          ) : (
            <div className="card-dashboard overflow-hidden p-0">
              <table className="w-full">
                <thead className="border-b border-[#27272A]">
                  <tr>
                    <th className="text-left text-xs uppercase tracking-widest text-[#A1A1AA] p-4">Email</th>
                    <th className="text-left text-xs uppercase tracking-widest text-[#A1A1AA] p-4">Estado</th>
                    <th className="text-left text-xs uppercase tracking-widest text-[#A1A1AA] p-4">Enviado</th>
                    <th className="text-right text-xs uppercase tracking-widest text-[#A1A1AA] p-4">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((invitation) => (
                    <tr key={invitation.id} className="border-b border-[#27272A] last:border-0 hover:bg-[#1A1A1A]">
                      <td className="p-4">
                        <span className="font-medium">{invitation.email}</span>
                      </td>
                      <td className="p-4">
                        <span className={`px-3 py-1 text-xs uppercase font-bold ${
                          invitation.status === 'confirmed' ? 'bg-green-500/20 text-green-500' :
                          invitation.status === 'opened' ? 'bg-yellow-500/20 text-yellow-500' :
                          invitation.status === 'sent' ? 'bg-blue-500/20 text-blue-500' :
                          'bg-[#27272A] text-[#A1A1AA]'
                        }`}>
                          {invitation.status === 'confirmed' ? 'Confirmada' :
                           invitation.status === 'opened' ? 'Abierta' :
                           invitation.status === 'sent' ? 'Enviada' : 'Pendiente'}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-[#A1A1AA]">
                          {invitation.sent_at ? new Date(invitation.sent_at).toLocaleDateString('es-AR') : '-'}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResend(invitation.id)}
                            className="text-blue-500 hover:text-blue-400"
                          >
                            <Send size={14} className="mr-1" />
                            Reenviar
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteInvitation(invitation.id)}
                            className="text-red-500 hover:text-red-400"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
        </AdminLayout>
      </SidebarProvider>
  );
};

export default EventInvitationsPage;
